import { Download, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ApiError } from '@/hooks/useApi';
import {
  useImportFileDownload,
  useImportSessionAction,
  useImportSessionIssues,
  useImportSessions,
  useImportTarget,
  useUploadImport,
} from '@/hooks/useImports';
import {
  IMPORT_TYPE_LABELS,
  type ImportSession,
  type ImportSessionStatus,
  type ImportTypeKey,
  isImportActive,
  isImportApplyOverdue,
  isImportCancellable,
} from '@/types/import';

const POLL_INTERVAL_MS = 4000;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const XLSX_EXTENSION = '.xlsx';
/** The results workbook holds every issue, so the page only previews enough to see what went wrong. */
const ISSUE_PREVIEW_LIMIT = 100;

const STATUS_VARIANTS: Record<ImportSessionStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  Uploaded: 'secondary',
  Validating: 'secondary',
  Validated: 'default',
  Rejected: 'destructive',
  Committing: 'secondary',
  Committed: 'default',
  Failed: 'destructive',
  Cancelled: 'outline',
};

function formatDateTime(value: string | null): string {
  return value === null ? '—' : new Date(value).toLocaleString();
}

export function ImportsPage() {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const [importType, setImportType] = useState<ImportTypeKey>('customerContacts');
  const [hasConfirmedCompany, setHasConfirmedCompany] = useState(false);
  const [sessionToApply, setSessionToApply] = useState<ImportSession | null>(null);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: target, isLoading: isTargetLoading } = useImportTarget();
  const { data: sessions = [] } = useImportSessions(POLL_INTERVAL_MS);
  const { data: issues = [], isLoading: areIssuesLoading } = useImportSessionIssues(
    expandedSessionId,
    ISSUE_PREVIEW_LIMIT,
  );

  const upload = useUploadImport();
  const confirm = useImportSessionAction('confirm');
  const cancel = useImportSessionAction('cancel');
  const download = useImportFileDownload();

  const activeSession = useMemo(() => sessions.find(isImportActive) ?? null, [sessions]);

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return <AdminOnlyEmptyState />;
  }

  const resetUploadForm = () => {
    setHasConfirmedCompany(false);

    if (fileInputRef.current !== null) {
      fileInputRef.current.value = '';
    }
  };

  // The file picker is the upload: choosing a workbook starts validation, so there is nothing left
  // to press afterwards and no chosen-but-not-uploaded state to explain.
  const handleFileChosen = (selected: File | null) => {
    if (selected === null) {
      return;
    }

    if (!selected.name.toLowerCase().endsWith(XLSX_EXTENSION)) {
      toast.error('Only .xlsx workbooks can be imported');
      resetUploadForm();
      return;
    }

    if (selected.size > MAX_FILE_SIZE_BYTES) {
      toast.error('The workbook is larger than 10 MB');
      resetUploadForm();
      return;
    }

    upload.mutate(
      { importType, file: selected },
      {
        onSuccess: () => {
          toast.success('The workbook is being checked, and is applied automatically if there are no errors');
          resetUploadForm();
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.message : 'The workbook could not be uploaded');
          resetUploadForm();
        },
      },
    );
  };

  const handleDownloadTemplate = () => {
    download.mutate(
      {
        path: `/templates/${importType}`,
        fileName: `${importType}-template.xlsx`,
      },
      { onError: () => toast.error('The template could not be downloaded') },
    );
  };

  const handleDownloadResults = (session: ImportSession) => {
    download.mutate(
      {
        path: `/sessions/${session.publicId}/results`,
        fileName: `${session.fileName.replace(/\.xlsx$/i, '')}-validation.xlsx`,
      },
      { onError: () => toast.error('The results could not be downloaded') },
    );
  };

  const handleCancel = (session: ImportSession) => {
    cancel.mutate(session.publicId, {
      onSuccess: () => toast.success('The import was cancelled'),
      onError: (error) => toast.error(error instanceof ApiError ? error.message : 'The import could not be cancelled'),
    });
  };

  const handleConfirm = () => {
    if (sessionToApply === null) {
      return;
    }

    confirm.mutate(sessionToApply.publicId, {
      onSuccess: () => toast.success('The import is being applied'),
      onError: (error) => toast.error(error instanceof ApiError ? error.message : 'The import could not be applied'),
    });

    setSessionToApply(null);
  };

  const canUpload = hasConfirmedCompany && activeSession === null && !upload.isPending;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="container max-w-7xl mx-auto p-6 space-y-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">Imports</h1>
          </div>
          <p className="text-muted-foreground">
            Bulk import customer contacts and customer collection settings from a spreadsheet
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Upload a workbook</CardTitle>
            <CardDescription>
              The whole workbook is checked before anything is changed. If it has no errors it is applied automatically;
              if it has any, nothing is changed and you can correct it and upload it again
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isTargetLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <Alert>
                <AlertTitle>Confirm the company</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>
                    This workbook will be imported into <strong>{target?.companyName}</strong>. Customers are matched by
                    account number.
                  </p>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="confirm-company"
                      checked={hasConfirmedCompany}
                      onCheckedChange={(checked) => setHasConfirmedCompany(checked === true)}
                    />
                    <Label htmlFor="confirm-company">I confirm the spreadsheet contains data for this company</Label>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2 md:max-w-sm">
              <Label htmlFor="import-type">What are you importing?</Label>
              <Select value={importType} onValueChange={(value) => setImportType(value as ImportTypeKey)}>
                <SelectTrigger id="import-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(IMPORT_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activeSession !== null && (
              <Alert>
                <AlertTitle>An import is running</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>
                    <strong>{activeSession.fileName}</strong> ({IMPORT_TYPE_LABELS[activeSession.importType]}), started
                    by {activeSession.createdByUserName ?? 'an administrator'} on{' '}
                    {formatDateTime(activeSession.createdAt)}, is being checked and applied. Only one import runs at a
                    time, so the next one can be uploaded once this one finishes.
                  </p>
                  {isImportCancellable(activeSession) && (
                    <Button variant="outline" size="sm" onClick={() => handleCancel(activeSession)}>
                      Cancel this import
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2">
                <Download className="h-4 w-4" />
                Download template
              </Button>
              <Button onClick={() => fileInputRef.current?.click()} disabled={!canUpload} className="gap-2">
                {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload and validate
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={XLSX_EXTENSION}
                onChange={(event) => handleFileChosen(event.target.files?.[0] ?? null)}
                className="hidden"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent imports</CardTitle>
            <CardDescription>Validation and applying both run in the background</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Rows</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">Warnings</TableHead>
                  <TableHead className="text-right">Duplicates</TableHead>
                  <TableHead className="text-right">Applied</TableHead>
                  <TableHead>Started by</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Finished</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center text-muted-foreground">
                      No imports yet
                    </TableCell>
                  </TableRow>
                )}
                {sessions.map((session) => (
                  <TableRow key={session.publicId}>
                    <TableCell className="font-medium">{session.fileName}</TableCell>
                    <TableCell>{IMPORT_TYPE_LABELS[session.importType]}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANTS[session.status]}>{session.status}</Badge>
                      {session.failureReason !== null && (
                        <p className="mt-1 text-xs text-muted-foreground">{session.failureReason}</p>
                      )}
                      {session.status === 'Rejected' && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Nothing was changed. Correct the workbook and upload it again
                        </p>
                      )}
                      {session.status === 'Validated' && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {isImportApplyOverdue(session) ? 'Checked, but not applied yet' : 'Being applied'}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{session.totalRows}</TableCell>
                    <TableCell className="text-right">{session.errorCount}</TableCell>
                    <TableCell className="text-right">{session.warningCount}</TableCell>
                    <TableCell className="text-right">{session.skippedDuplicateCount}</TableCell>
                    <TableCell className="text-right">
                      {session.insertedCount + session.updatedCount === 0
                        ? '—'
                        : `${session.insertedCount} new, ${session.updatedCount} updated`}
                    </TableCell>
                    <TableCell>{session.createdByUserName ?? '—'}</TableCell>
                    <TableCell>{formatDateTime(session.createdAt)}</TableCell>
                    <TableCell>{formatDateTime(session.completedAt)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap justify-end gap-2">
                        {session.errorCount + session.warningCount > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setExpandedSessionId(expandedSessionId === session.publicId ? null : session.publicId)
                            }
                          >
                            {expandedSessionId === session.publicId ? 'Hide issues' : 'View issues'}
                          </Button>
                        )}
                        {session.errorCount + session.warningCount > 0 && (
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadResults(session)}>
                            Download results
                          </Button>
                        )}
                        {/* A clean validation applies itself, so this only appears for an import
                            whose apply never got picked up. */}
                        {isImportApplyOverdue(session) && (
                          <Button variant="ghost" size="sm" onClick={() => setSessionToApply(session)}>
                            Apply now
                          </Button>
                        )}
                        {isImportCancellable(session) && (
                          <Button variant="outline" size="sm" onClick={() => handleCancel(session)}>
                            Cancel
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {expandedSessionId !== null && (
              <div className="mt-6 space-y-2">
                <h2 className="text-lg font-semibold">Issues</h2>
                {issues.length === ISSUE_PREVIEW_LIMIT && (
                  <p className="text-sm text-muted-foreground">
                    Showing the first {ISSUE_PREVIEW_LIMIT} issues. Download the results to see them all.
                  </p>
                )}
                {areIssuesLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">Row</TableHead>
                        <TableHead className="w-28">Severity</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {issues.map((issue) => (
                        <TableRow key={`${issue.rowNumber}-${issue.fieldName}-${issue.message}`}>
                          <TableCell>{issue.rowNumber}</TableCell>
                          <TableCell>
                            <Badge variant={issue.severity === 'Error' ? 'destructive' : 'secondary'}>
                              {issue.severity}
                            </Badge>
                          </TableCell>
                          <TableCell>{issue.fieldName ?? '—'}</TableCell>
                          <TableCell>{issue.fieldValue ?? '—'}</TableCell>
                          <TableCell>{issue.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={sessionToApply !== null} onOpenChange={(open) => !open && setSessionToApply(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply this import?</AlertDialogTitle>
            <AlertDialogDescription>
              {sessionToApply?.totalRows} rows from {sessionToApply?.fileName} will be applied to {target?.companyName}.
              Contacts and collection settings are added or updated; nothing is deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Apply import</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
